import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import { setOnlineUser } from '../features/user/userSlice';
import { getLocalStorageData } from '../shared/commonFunction';

let sharedSocketInstance = null;
let sharedSocketBaseUrl = null;
let sharedSocketToken = null;

export const getSharedSocket = () => sharedSocketInstance;

const getOrCreateSharedSocket = (baseUrl, token) => {
    const nextToken = token || null;

    if (
        sharedSocketInstance &&
        sharedSocketBaseUrl === baseUrl &&
        sharedSocketToken === nextToken
    ) {
        return sharedSocketInstance;
    }

    if (sharedSocketInstance) {
        sharedSocketInstance.removeAllListeners();
        sharedSocketInstance.disconnect();
    }

    sharedSocketBaseUrl = baseUrl;
    sharedSocketToken = nextToken;
    sharedSocketInstance = io(baseUrl, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        auth: { token: nextToken },
    });

    return sharedSocketInstance;
};

const useSocket = () => {
    const socketRef = useRef(null);
    const [socket, setSocket] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const isLogin = useSelector((state) => state.login.isLogin);

    const BASE_URL = import.meta.env.VITE_CHAT_BASE_URL;
    const dispatch = useDispatch();

    useEffect(() => {
        const token = getLocalStorageData('token');
        const socketInstance = getOrCreateSharedSocket(BASE_URL, token);

        socketRef.current = socketInstance;
        setSocket(socketInstance);

        const markReady = () => {
            setIsReady(true);
            setSocket(socketInstance);
        };

        const handleConnect = () => {
            markReady();
        };

        const handleDisconnect = () => {
            setIsReady(false);
        };

        const handleConnectError = (error) => {
            console.error('Connection Error:', error);
            setIsReady(false);
        };

        const handleOnlineUser = (data) => {
            dispatch(setOnlineUser(data));
        };

        socketInstance.on('connect', handleConnect);
        socketInstance.on('disconnect', handleDisconnect);
        socketInstance.on('connect_error', handleConnectError);
        socketInstance.on('onlineUser', handleOnlineUser);

        if (socketInstance.connected) {
            markReady();
        } else {
            setIsReady(false);
        }

        return () => {
            socketInstance.off('connect', handleConnect);
            socketInstance.off('disconnect', handleDisconnect);
            socketInstance.off('connect_error', handleConnectError);
            socketInstance.off('onlineUser', handleOnlineUser);
        };
    }, [BASE_URL, dispatch, isLogin]);

    return { socket, socketRef, isReady };
};

export default useSocket;
